/**
 * MapGeoCanvas — Geographic map renderer using MapLibre GL JS + Protomaps.
 *
 * Renders a real-world map with game nodes as markers and connections as lines.
 * Drop-in alternative to MapCanvas for renderer_type === "geo".
 * Includes Nominatim-powered location search.
 */

import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ZoomIn, ZoomOut, Maximize2, Search, Loader2, Lock, Unlock, Crosshair } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MapNode, MapConnection, GeoRendererConfig, MapDisplayDefaults, MapLayer } from "@/lib/map";
import { DEFAULT_DISPLAY, CONNECTION_STYLES } from "@/lib/map";
import { useLocale } from "@/hooks/useLocale";

/** Resolve a CSS variable like `hsl(var(--primary))` to a computed color string MapLibre can use */
function resolveCssColor(cssValue: string): string {
  if (!cssValue.includes("var(--")) return cssValue;
  try {
    const match = cssValue.match(/var\(--([^)]+)\)/);
    if (!match) return cssValue;
    const varName = `--${match[1]}`;
    const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    if (!raw) return cssValue;
    // raw is typically "H S% L%" — wrap in hsl()
    if (/^\d/.test(raw)) return `hsl(${raw})`;
    return raw;
  } catch {
    return cssValue;
  }
}

const DEFAULT_GEO_CONFIG: GeoRendererConfig = {
  center: [0, 20],
  zoom: 2,
};

const PROTOMAPS_DARK_STYLE = "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
const PROTOMAPS_LIGHT_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

interface MapGeoCanvasProps {
  geoConfig?: GeoRendererConfig;
  currentNodes: MapNode[];
  selectedNode: string | null;
  connections: MapConnection[];
  connectionMode: boolean;
  connectionFrom: string | null;
  placingMode: "point" | "area" | "polygon" | null;
  polygonVertices?: { lng: number; lat: number }[];
  layers?: MapLayer[];
  layerPlacingIndex?: number | null;
  onLayerBoundsSet?: (index: number, bounds: [[number, number], [number, number]]) => void;
  onNodeClick?: (nodeId: string, e: { clientX: number; clientY: number }) => void;
  onNodeDrag?: (nodeId: string, lng: number, lat: number) => void;
  onNodeDragEnd?: (nodeId: string) => void;
  onMapClick?: (lng: number, lat: number, e: { clientX: number; clientY: number }) => void;
  onMapDoubleClick?: (lng: number, lat: number, e: { clientX: number; clientY: number }) => void;
  onViewChange?: (center: [number, number], zoom: number) => void;
  onSetDefaultView?: (center: [number, number], zoom: number) => void;
  onBoundsChange?: (bounds: [[number, number], [number, number]] | undefined) => void;
  onUploadMap?: () => void;
  onGenerateMap?: () => void;
  uploading?: boolean;
  displayDefaults?: MapDisplayDefaults;
  darkMode?: boolean;
  readOnly?: boolean;
  highlightNodeId?: string | null;
}

// Register PMTiles protocol once
let protocolRegistered = false;
function ensureProtocol() {
  if (protocolRegistered) return;
  try {
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);
    protocolRegistered = true;
  } catch (err) {
    console.warn("PMTiles protocol registration failed:", err);
  }
}

export const MapGeoCanvas = React.memo(function MapGeoCanvas({
  geoConfig,
  currentNodes,
  selectedNode,
  connections,
  connectionMode,
  connectionFrom,
  placingMode,
  polygonVertices,
  layers,
  layerPlacingIndex,
  onLayerBoundsSet,
  onNodeClick,
  onNodeDrag,
  onNodeDragEnd,
  onMapClick,
  onMapDoubleClick,
  onViewChange,
  onSetDefaultView,
  onBoundsChange,
  onUploadMap,
  onGenerateMap,
  uploading,
  displayDefaults,
  darkMode,
  readOnly,
  highlightNodeId,
}: MapGeoCanvasProps) {
  const { t } = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const markerCirclesRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(!!geoConfig?.bounds);
  const defaults = displayDefaults || DEFAULT_DISPLAY;
  const config = geoConfig || DEFAULT_GEO_CONFIG;

  // Stable config ref — only used for initial map setup; view changes are persisted via onViewChange
  const configRef = useRef(config);
  configRef.current = config;

  // Sync isLocked with incoming prop
  useEffect(() => { setIsLocked(!!config.bounds); }, [!!config.bounds]);

  // Debounce ref for view persistence
  const viewChangeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for stable closure access in map event handlers
  const placingModeRef = useRef(placingMode);
  placingModeRef.current = placingMode;
  const onMapClickRef = useRef(onMapClick);
  onMapClickRef.current = onMapClick;
  const onMapDoubleClickRef = useRef(onMapDoubleClick);
  onMapDoubleClickRef.current = onMapDoubleClick;
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;
  const onNodeDragRef = useRef(onNodeDrag);
  onNodeDragRef.current = onNodeDrag;
  const onNodeDragEndRef = useRef(onNodeDragEnd);
  onNodeDragEndRef.current = onNodeDragEnd;
  const connectionModeRef = useRef(connectionMode);
  connectionModeRef.current = connectionMode;
  const connectionFromRef = useRef(connectionFrom);
  connectionFromRef.current = connectionFrom;

  // O(1) node lookup for connections
  const nodeMap = useMemo(() => new Map(currentNodes.map(n => [n.id, n])), [currentNodes]);
  const nodeMapRef = useRef(nodeMap);
  nodeMapRef.current = nodeMap;

  // Track visual fingerprint per node to detect image/label changes
  const nodeFingerprints = useRef<Map<string, string>>(new Map());

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  // Initialize map
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Ensure container has dimensions before initializing
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.warn("MapGeoCanvas: container has zero dimensions, retrying...");
      const retryTimer = setTimeout(() => {
        // Force a re-render by toggling state
        setMapError(null);
      }, 100);
      return () => clearTimeout(retryTimer);
    }

    ensureProtocol();
    setMapError(null);

    const cfg = configRef.current;
    const styleUrl = cfg.style_url || (darkMode ? PROTOMAPS_DARK_STYLE : PROTOMAPS_LIGHT_STYLE);

    let map: maplibregl.Map;
    const mapOptions: maplibregl.MapOptions = {
      container,
      style: styleUrl,
      center: cfg.center,
      zoom: cfg.zoom,
      attributionControl: false,
    };
    if (cfg.bounds) {
      mapOptions.maxBounds = cfg.bounds;
    }

    // Enable RTL text rendering for Hebrew/Arabic map labels
    try {
      maplibregl.setRTLTextPlugin(
        "https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.min.js",
        true,
      );
    } catch (_) { /* plugin already set */ }

    try {
      map = new maplibregl.Map(mapOptions);
    } catch (err: any) {
      console.error("MapGeoCanvas: MapLibre init error", err);
      setMapError(err?.message || "Failed to initialize map");
      return;
    }

    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");

    map.on("load", () => {
      console.log("MapGeoCanvas: map loaded successfully");
      setMapReady(true);

      map.addSource("connections", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "connections-layer",
        type: "line",
        source: "connections",
        paint: {
          "line-color": ["get", "color"],
          "line-width": ["get", "width"],
          "line-opacity": 0.7,
          "line-dasharray": ["literal", [1]], // default solid — updated per-feature below
        },
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
      });

      // Connection labels layer
      map.addLayer({
        id: "connections-labels",
        type: "symbol",
        source: "connections",
        layout: {
          "symbol-placement": "line-center",
          "text-field": ["get", "label"],
          "text-size": 11,
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": resolveCssColor("hsl(var(--foreground))"),
          "text-halo-color": resolveCssColor("hsl(var(--background))"),
          "text-halo-width": 2,
          "text-opacity": 0.85,
        },
        filter: ["!=", ["get", "label"], ""],
      });

      // Polygon preview source/layers
      map.addSource("polygon-preview", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "polygon-preview-fill",
        type: "fill",
        source: "polygon-preview",
        paint: {
          "fill-color": resolveCssColor("hsl(var(--primary))"),
          "fill-opacity": 0.15,
        },
        filter: ["==", "$type", "Polygon"],
      });
      map.addLayer({
        id: "polygon-preview-line",
        type: "line",
        source: "polygon-preview",
        paint: {
          "line-color": resolveCssColor("hsl(var(--primary))"),
          "line-width": 2,
          "line-dasharray": [4, 3],
        },
      });
      map.addLayer({
        id: "polygon-preview-points",
        type: "circle",
        source: "polygon-preview",
        paint: {
          "circle-radius": 4,
          "circle-color": resolveCssColor("hsl(var(--primary))"),
          "circle-stroke-color": resolveCssColor("hsl(var(--background))"),
          "circle-stroke-width": 1,
        },
        filter: ["==", "$type", "Point"],
      });

      // Polygon areas source/layers for existing polygon nodes
      map.addSource("polygon-areas", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "polygon-areas-fill",
        type: "fill",
        source: "polygon-areas",
        paint: {
          "fill-color": ["get", "color"],
          "fill-opacity": 0.12,
        },
      });
      map.addLayer({
        id: "polygon-areas-outline",
        type: "line",
        source: "polygon-areas",
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2,
          "line-opacity": 0.6,
        },
      });

      // Saved bounds outline (dashed rectangle)
      map.addSource("saved-bounds", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "saved-bounds-outline",
        type: "line",
        source: "saved-bounds",
        paint: {
          "line-color": resolveCssColor("hsl(var(--primary))"),
          "line-width": 2,
          "line-opacity": 0.5,
          "line-dasharray": [6, 4],
        },
        layout: { "line-cap": "round", "line-join": "round" },
      });

      // Pending connection line (dashed, from source node to cursor)
      map.addSource("pending-connection", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "pending-connection-line",
        type: "line",
        source: "pending-connection",
        paint: {
          "line-color": resolveCssColor("hsl(var(--primary))"),
          "line-width": 2,
          "line-opacity": 0.6,
          "line-dasharray": [4, 4],
        },
        layout: { "line-cap": "round" },
      });

      // Render saved bounds outline if bounds exist at load time
      if (cfg.bounds) {
        const [[w, s], [e, n]] = cfg.bounds;
        (map.getSource("saved-bounds") as maplibregl.GeoJSONSource)?.setData({
          type: "FeatureCollection",
          features: [{
            type: "Feature",
            properties: {},
            geometry: {
              type: "Polygon",
              coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]],
            },
          }],
        });
      }
    });

    map.on("error", (e) => {
      console.error("MapGeoCanvas: map error", e.error);
    });

    map.on("click", (e) => {
      if (placingModeRef.current && onMapClickRef.current) {
        onMapClickRef.current(e.lngLat.lng, e.lngLat.lat, {
          clientX: e.originalEvent.clientX,
          clientY: e.originalEvent.clientY,
        });
      }
    });

    // Update pending connection line on mouse move
    map.on("mousemove", (e) => {
      if (!connectionModeRef.current || !connectionFromRef.current) return;
      const fromNode = nodeMapRef.current.get(connectionFromRef.current);
      if (!fromNode?.display?.lng || !fromNode?.display?.lat) return;
      const source = map.getSource("pending-connection") as maplibregl.GeoJSONSource | undefined;
      if (!source) return;
      source.setData({
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: [
              [fromNode.display.lng, fromNode.display.lat],
              [e.lngLat.lng, e.lngLat.lat],
            ],
          },
        }],
      });
    });

    map.on("dblclick", (e) => {
      if ((placingModeRef.current === "polygon" || placingModeRef.current === "area") && onMapDoubleClickRef.current) {
        e.preventDefault();
        onMapDoubleClickRef.current(e.lngLat.lng, e.lngLat.lat, {
          clientX: e.originalEvent.clientX,
          clientY: e.originalEvent.clientY,
        });
      }
    });

    // Debounced view change persistence (2s)
    map.on("moveend", () => {
      if (onViewChange) {
        if (viewChangeTimer.current) clearTimeout(viewChangeTimer.current);
        viewChangeTimer.current = setTimeout(() => {
          const center = map.getCenter();
          onViewChange([center.lng, center.lat], map.getZoom());
        }, 2000);
      }
    });

    mapRef.current = map;

    return () => {
      if (viewChangeTimer.current) clearTimeout(viewChangeTimer.current);
      markersRef.current.forEach(m => m.remove());
      markersRef.current.clear();
      markerCirclesRef.current.clear();
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode, mapError, geoConfig?.style_url]);

  // Update cursor when placing mode changes — keep dragPan enabled for navigation
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (placingMode) {
      map.getCanvas().style.cursor = "crosshair";
      // Keep dragPan enabled so users can pan while placing
      map.dragPan.enable();
    } else {
      map.getCanvas().style.cursor = "";
      map.dragPan.enable();
    }
  }, [placingMode, mapReady]);

  // Update markers when nodes change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const existingIds = new Set(markersRef.current.keys());
    const nodeIds = new Set(currentNodes.map(n => n.id));

    existingIds.forEach(id => {
      if (!nodeIds.has(id)) {
        markersRef.current.get(id)?.remove();
        markersRef.current.delete(id);
        markerCirclesRef.current.delete(id);
      }
    });

    currentNodes.forEach(node => {
      const lng = node.display?.lng;
      const lat = node.display?.lat;
      if (lng == null || lat == null) return;

      const isSelected = node.id === selectedNode;
      const isHighlighted = node.id === highlightNodeId;
      const isConnectionSource = node.id === connectionFrom;
      const nodeImageUrl = node.display?.image_url || node.map_image_url;
      const showLabel = node.display?.show_text !== undefined ? node.display.show_text : defaults.show_text;
      const labelColor = node.display?.text_color || defaults.text_color;
      const fingerprint = `${node.label}|${nodeImageUrl || ""}|${showLabel}|${labelColor}|${isHighlighted}`;

      let marker = markersRef.current.get(node.id);
      const oldFingerprint = nodeFingerprints.current.get(node.id);

      // If visual props changed, destroy and recreate the marker
      if (marker && oldFingerprint && oldFingerprint !== fingerprint) {
        marker.remove();
        markersRef.current.delete(node.id);
        marker = undefined;
      }
      nodeFingerprints.current.set(node.id, fingerprint);

      if (marker) {
        marker.setLngLat([lng, lat]);
        // Update selection ring on the INNER circle, not the wrapper
        const circle = markerCirclesRef.current.get(node.id);
        if (circle) {
          circle.className = cn(
            "map-geo-marker-circle",
            isSelected && "ring-2 ring-primary ring-offset-1",
            isHighlighted && "ring-2 ring-green-400 ring-offset-1 animate-pulse",
            isConnectionSource && "ring-2 ring-accent",
          );
        }
        // Update draggable state
        marker.setDraggable(!readOnly && !connectionMode && !placingMode);
      } else {
        // Wrapper div that allows label to overflow
        const wrapper = document.createElement("div");
        wrapper.style.cursor = "pointer";
        wrapper.style.display = "flex";
        wrapper.style.flexDirection = "column";
        wrapper.style.alignItems = "center";

        const el = document.createElement("div");
        el.className = cn(
          "map-geo-marker-circle",
          isSelected && "ring-2 ring-primary ring-offset-1",
          isHighlighted && "ring-2 ring-green-400 ring-offset-1 animate-pulse",
          isConnectionSource && "ring-2 ring-accent",
        );
        const markerSize = defaults.point_size + 8;
        el.style.width = `${markerSize}px`;
        el.style.height = `${markerSize}px`;
        el.style.borderRadius = "50%";
        el.style.border = `2px solid ${resolveCssColor("hsl(var(--primary-foreground))")}`;
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.3)";
        el.style.overflow = "hidden";
        el.style.flexShrink = "0";

        if (nodeImageUrl) {
          el.style.backgroundColor = "transparent";
          const img = document.createElement("img");
          img.src = nodeImageUrl;
          img.alt = node.label;
          img.style.width = "100%";
          img.style.height = "100%";
          img.style.objectFit = "cover";
          img.style.borderRadius = "50%";
          el.appendChild(img);
        } else {
          el.style.backgroundColor = resolveCssColor("hsl(var(--primary))");
        }

        wrapper.appendChild(el);

        // Label — rendered OUTSIDE the clipped circle so it's always visible
        if (showLabel) {
          const label = document.createElement("div");
          label.className = "text-[10px] font-medium whitespace-nowrap text-center pointer-events-none";
          label.style.color = labelColor;
          label.style.textShadow = "0 1px 3px rgba(0,0,0,0.5)";
          label.style.marginTop = "2px";
          label.dir = "auto";
          label.textContent = node.label;
          wrapper.appendChild(label);
        }

        // Use ref-based callbacks for stable closures
        const nodeId = node.id;
        wrapper.addEventListener("click", (e) => {
          e.stopPropagation();
          onNodeClickRef.current?.(nodeId, { clientX: e.clientX, clientY: e.clientY });
        });

        // Anchor at 'top' so the circle center aligns with the coordinate
        // The circle is at the top of the wrapper; label hangs below
        marker = new maplibregl.Marker({
          element: wrapper,
          draggable: !readOnly && !connectionMode && !placingMode,
          anchor: "top",
          offset: [0, -(markerSize / 2)],
        })
          .setLngLat([lng, lat])
          .addTo(map);

        markerCirclesRef.current.set(node.id, el);

        // Drag: MapLibre moves the marker visually. We only track final position.
        // Use a throttled drag callback to reduce intermediate updates.
        let dragThrottleTimer: ReturnType<typeof setTimeout> | null = null;
        marker.on("drag", () => {
          if (dragThrottleTimer) return; // Skip if throttled
          dragThrottleTimer = setTimeout(() => { dragThrottleTimer = null; }, 100);
          const pos = marker!.getLngLat();
          onNodeDragRef.current?.(nodeId, pos.lng, pos.lat);
        });
        marker.on("dragend", () => {
          if (dragThrottleTimer) { clearTimeout(dragThrottleTimer); dragThrottleTimer = null; }
          const pos = marker!.getLngLat();
          onNodeDragRef.current?.(nodeId, pos.lng, pos.lat);
          onNodeDragEndRef.current?.(nodeId);
        });

        markersRef.current.set(node.id, marker);
      }
    });
  }, [currentNodes, selectedNode, connectionFrom, mapReady, connectionMode, placingMode, defaults.point_size]);

  // Update connections GeoJSON
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const source = map.getSource("connections") as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    const features = connections
      .map(conn => {
        const fromNode = nodeMap.get(conn.from);
        const toNode = nodeMap.get(conn.to);
        if (!fromNode?.display?.lng || !fromNode?.display?.lat || !toNode?.display?.lng || !toNode?.display?.lat) return null;

        const style = CONNECTION_STYLES[conn.style] || CONNECTION_STYLES.road;
        // Parse dash string "4 4" → [4, 4] array for MapLibre
        const dashArray = style.dash ? style.dash.split(" ").map(Number) : [];
        return {
          type: "Feature" as const,
          properties: {
            id: conn.id,
            color: style.color,
            width: style.width,
            label: conn.label || "",
            dash: dashArray.length > 0 ? JSON.stringify(dashArray) : "",
          },
          geometry: {
            type: "LineString" as const,
            coordinates: [
              [fromNode.display.lng, fromNode.display.lat],
              [toNode.display.lng, toNode.display.lat],
            ],
          },
        };
      })
      .filter(Boolean);

    source.setData({ type: "FeatureCollection", features: features as any[] });
  }, [connections, nodeMap, mapReady]);

  // Update polygon preview GeoJSON
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const source = map.getSource("polygon-preview") as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    const features: any[] = [];
    if (polygonVertices && polygonVertices.length > 0) {
      // Points for each vertex
      polygonVertices.forEach((v, i) => {
        features.push({
          type: "Feature",
          properties: { index: i },
          geometry: { type: "Point", coordinates: [v.lng, v.lat] },
        });
      });
      // Line connecting vertices
      const coords = polygonVertices.map(v => [v.lng, v.lat]);
      if (polygonVertices.length >= 2) {
        features.push({
          type: "Feature",
          properties: {},
          geometry: { type: "LineString", coordinates: coords },
        });
      }
      // Closed polygon preview when >= 3 vertices
      if (polygonVertices.length >= 3) {
        features.push({
          type: "Feature",
          properties: {},
          geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] },
        });
      }
    }
    source.setData({ type: "FeatureCollection", features });
  }, [polygonVertices, mapReady]);

  // Update polygon areas for existing polygon nodes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const source = map.getSource("polygon-areas") as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    const features: any[] = [];
    currentNodes.forEach(node => {
      const pts = node.display?.polygon_points;
      if (!pts || pts.length < 3 || node.node_type !== "area") return;
      // polygon_points on geo maps store {x: lng, y: lat} (geo coords)
      const coords = pts.map(p => [p.x, p.y]);
      const fillColor = node.display?.area_fill_color || node.display?.outline_color || resolveCssColor("hsl(var(--primary))");
      features.push({
        type: "Feature",
        properties: { id: node.id, color: fillColor },
        geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] },
      });
    });
    source.setData({ type: "FeatureCollection", features });
  }, [currentNodes, mapReady]);

  // ─── Render image overlay layers ───
  const layerIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const desiredIds = new Set<string>();

    // Add/update layers
    (layers || []).forEach((layer, idx) => {
      const sourceId = `overlay-layer-${layer.id}`;
      const layerId = `overlay-raster-${layer.id}`;
      desiredIds.add(layer.id);

      if (!layer.visible || !layer.bounds || !layer.imageUrl) {
        // Remove if exists
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        return;
      }

      const [[swLng, swLat], [neLng, neLat]] = layer.bounds;
      // MapLibre image source expects coordinates as [TL, TR, BR, BL]
      const coordinates: [[number, number], [number, number], [number, number], [number, number]] = [
        [swLng, neLat],  // top-left
        [neLng, neLat],  // top-right
        [neLng, swLat],  // bottom-right
        [swLng, swLat],  // bottom-left
      ];

      const minZoom = layer.minZoom ?? 0;
      const maxZoom = layer.maxZoom ?? 22;
      const scalable = layer.scalable !== false; // default true

      if (map.getSource(sourceId)) {
        // Update existing source coordinates and URL
        const src = map.getSource(sourceId) as any;
        if (src.updateImage) {
          src.updateImage({ url: layer.imageUrl, coordinates });
        }
        // Update layer paint
        if (map.getLayer(layerId)) {
          // Zoom-dependent opacity: fade in from minZoom-1 to minZoom, fade out from maxZoom to maxZoom+1
          map.setPaintProperty(layerId, "raster-opacity", [
            "interpolate", ["linear"], ["zoom"],
            Math.max(0, minZoom - 1), 0,
            minZoom, layer.opacity,
            maxZoom, layer.opacity,
            Math.min(22, maxZoom + 1), 0,
          ]);
        }
      } else {
        // Create new source + layer
        map.addSource(sourceId, {
          type: "image",
          url: layer.imageUrl,
          coordinates,
        });
        map.addLayer({
          id: layerId,
          type: "raster",
          source: sourceId,
          paint: {
            "raster-opacity": [
              "interpolate", ["linear"], ["zoom"],
              Math.max(0, minZoom - 1), 0,
              minZoom, layer.opacity,
              maxZoom, layer.opacity,
              Math.min(22, maxZoom + 1), 0,
            ],
          },
        });
      }
    });

    // Remove layers that are no longer in the list
    layerIdsRef.current.forEach(oldId => {
      if (!desiredIds.has(oldId)) {
        const layerId = `overlay-raster-${oldId}`;
        const sourceId = `overlay-layer-${oldId}`;
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      }
    });
    layerIdsRef.current = desiredIds;
  }, [layers, mapReady]);

  // ─── Layer placement mode (two-click bounds) ───
  const layerPlacingCornerRef = useRef<[number, number] | null>(null);
  const layerPlacingIndexRef = useRef(layerPlacingIndex);
  layerPlacingIndexRef.current = layerPlacingIndex;
  const onLayerBoundsSetRef = useRef(onLayerBoundsSet);
  onLayerBoundsSetRef.current = onLayerBoundsSet;

  // Layer placement preview rectangle
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Add source/layer for layer placement preview if not exists
    if (!map.getSource("layer-placement-preview")) {
      map.addSource("layer-placement-preview", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "layer-placement-preview-fill",
        type: "fill",
        source: "layer-placement-preview",
        paint: {
          "fill-color": resolveCssColor("hsl(var(--primary))"),
          "fill-opacity": 0.15,
        },
      });
      map.addLayer({
        id: "layer-placement-preview-line",
        type: "line",
        source: "layer-placement-preview",
        paint: {
          "line-color": resolveCssColor("hsl(var(--primary))"),
          "line-width": 2,
          "line-dasharray": [4, 3],
        },
      });
    }

    if (layerPlacingIndex == null) {
      layerPlacingCornerRef.current = null;
      const src = map.getSource("layer-placement-preview") as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData({ type: "FeatureCollection", features: [] });
    }
  }, [layerPlacingIndex, mapReady]);

  // Mouse move for layer placement preview
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const onMove = (e: maplibregl.MapMouseEvent) => {
      if (layerPlacingIndexRef.current == null) return;
      const corner1 = layerPlacingCornerRef.current;
      if (!corner1) return;
      const src = map.getSource("layer-placement-preview") as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      const [lng1, lat1] = corner1;
      const lng2 = e.lngLat.lng;
      const lat2 = e.lngLat.lat;
      src.setData({
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [[[lng1, lat1], [lng2, lat1], [lng2, lat2], [lng1, lat2], [lng1, lat1]]],
          },
        }],
      });
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (layerPlacingIndexRef.current == null) return;
      const corner1 = layerPlacingCornerRef.current;
      if (!corner1) {
        // First click — set first corner
        layerPlacingCornerRef.current = [e.lngLat.lng, e.lngLat.lat];
        return;
      }
      // Second click — complete bounds
      const [lng1, lat1] = corner1;
      const lng2 = e.lngLat.lng;
      const lat2 = e.lngLat.lat;
      const bounds: [[number, number], [number, number]] = [
        [Math.min(lng1, lng2), Math.min(lat1, lat2)],
        [Math.max(lng1, lng2), Math.max(lat1, lat2)],
      ];
      onLayerBoundsSetRef.current?.(layerPlacingIndexRef.current, bounds);
      layerPlacingCornerRef.current = null;
      // Clear preview
      const src = map.getSource("layer-placement-preview") as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData({ type: "FeatureCollection", features: [] });
    };

    map.on("mousemove", onMove);
    map.on("click", onClick);
    return () => {
      map.off("mousemove", onMove);
      map.off("click", onClick);
    };
  }, [mapReady]);

  // Clear pending connection line when connectionFrom resets
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const source = map.getSource("pending-connection") as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    if (!connectionFrom) {
      source.setData({ type: "FeatureCollection", features: [] });
    }
  }, [connectionFrom, mapReady]);


  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || !mapRef.current) return;

    // First check placed nodes by label
    const query = searchQuery.trim().toLowerCase();
    const matchedNode = currentNodes.find(n =>
      n.label.toLowerCase().includes(query) && n.display?.lng != null && n.display?.lat != null
    );
    if (matchedNode && matchedNode.display?.lng != null && matchedNode.display?.lat != null) {
      mapRef.current.flyTo({ center: [matchedNode.display.lng, matchedNode.display.lat], zoom: Math.max(mapRef.current.getZoom(), 10), duration: 1500 });
      onNodeClickRef.current?.(matchedNode.id, { clientX: 0, clientY: 0 });
      return;
    }

    // Fall back to Nominatim
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1`, {
        headers: { "Accept-Language": "en" },
      });
      const data = await res.json();
      if (data.length > 0) {
        const { lon, lat } = data[0];
        mapRef.current.flyTo({ center: [parseFloat(lon), parseFloat(lat)], zoom: 10, duration: 2000 });
      }
    } catch {
      // silently fail search
    } finally {
      setSearching(false);
    }
  }, [searchQuery, currentNodes]);

  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const handleResetView = () => {
    mapRef.current?.flyTo({ center: configRef.current.center, zoom: configRef.current.zoom });
  };

  const handleSetDefaultView = () => {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    onSetDefaultView?.([center.lng, center.lat], zoom);
  };

  const updateSavedBoundsOutline = useCallback((bounds: [[number, number], [number, number]] | undefined) => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource("saved-bounds") as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (!bounds) {
      src.setData({ type: "FeatureCollection", features: [] });
      return;
    }
    const [[w, s], [e, n]] = bounds;
    src.setData({
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]],
        },
      }],
    });
  }, [mapReady]);

  const handleToggleBounds = () => {
    const map = mapRef.current;
    if (!map) return;
    if (isLocked) {
      // Unlock — remove maxBounds but keep dashed outline visible
      map.setMaxBounds(undefined as any);
      setIsLocked(false);
      onBoundsChange?.(undefined);
      // Keep the outline so user sees where saved view was
    } else {
      // Lock to current view
      const bounds = map.getBounds();
      const newBounds: [[number, number], [number, number]] = [
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()],
      ];
      map.setMaxBounds(newBounds);
      setIsLocked(true);
      onBoundsChange?.(newBounds);
      updateSavedBoundsOutline(newBounds);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
    <div className="relative w-full h-full min-h-[400px]" style={{ minHeight: "400px" }}>
      {/* MapLibre container — must have explicit dimensions */}
      <div ref={containerRef} className="absolute inset-0" style={{ width: "100%", height: "100%" }} />

      {/* Error state */}
      {mapError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
          <div className="text-center p-4">
            <p className="text-sm text-destructive font-medium mb-2">Map failed to load</p>
            <p className="text-xs text-muted-foreground mb-3">{mapError}</p>
            <Button variant="outline" size="sm" onClick={() => setMapError(null)}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="absolute top-2 left-2 z-10 flex gap-1">
        <form
          onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
          className="flex gap-1"
        >
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.map_search_nodes_places_placeholder}
            className="h-7 w-40 text-xs bg-background/90 backdrop-blur-sm border-border/50"
          />
          <Button type="submit" variant="secondary" size="icon" className="h-7 w-7 shadow border border-border/50" aria-label="Search" disabled={searching}>
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </Button>
        </form>
      </div>

      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
        <Button variant="secondary" size="icon" className="h-7 w-7 shadow border border-border/50" aria-label="Zoom in" onClick={handleZoomIn}>
          <ZoomIn className="w-3.5 h-3.5" />
        </Button>
        <Button variant="secondary" size="icon" className="h-7 w-7 shadow border border-border/50" aria-label="Zoom out" onClick={handleZoomOut}>
          <ZoomOut className="w-3.5 h-3.5" />
        </Button>
        <Button variant="secondary" size="icon" className="h-7 w-7 shadow border border-border/50" aria-label="Reset view" onClick={handleResetView}>
          <Maximize2 className="w-3.5 h-3.5" />
        </Button>
        <div className="w-full h-px bg-border/30 my-0.5" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Save current view as default"
              className="h-7 w-7 shadow border border-border/50"
              onClick={handleSetDefaultView}
            >
              <Crosshair className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="text-xs">Save current view as default</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={isLocked ? "default" : "secondary"}
              size="icon"
              aria-label={isLocked ? "Unlock map bounds" : "Lock to current view"}
              className="h-7 w-7 shadow border border-border/50"
              onClick={handleToggleBounds}
            >
              {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="text-xs">{isLocked ? "Unlock map bounds" : "Lock to current view"}</TooltipContent>
        </Tooltip>
      </div>

      {/* Placing mode indicator */}
      {placingMode && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 bg-primary/90 text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
          {placingMode === "point" && "Click on map to place a point"}
          {(placingMode === "area" || placingMode === "polygon") && (
            polygonVertices && polygonVertices.length > 0
              ? `${polygonVertices.length} vertices — double-click or click first vertex to close`
              : "Click to add vertices, double-click to close"
          )}
        </div>
      )}

      {/* Connection mode indicator */}
      {connectionMode && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 bg-accent/90 text-accent-foreground text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
          {connectionFrom ? "Click another node to connect" : "Click a node to start connection"}
        </div>
      )}

      {/* Layer placing indicator */}
      {layerPlacingIndex != null && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 bg-primary/90 text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
          Click two corners on the map to place the overlay
        </div>
      )}
    </div>
    </TooltipProvider>
  );
});
